import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import {
  formatNivel,
  formatPorcentaje,
  formatValor,
} from "../../../../../modulos/monitoreo/sesion/formatIncidencia";
import {
  calcularNivel,
  estadoDeUnValor,
  GRUPO_BENEFICO,
} from "../../../../../modulos/monitoreo/sesion/evaluacion";
import { MonitoreoReferencia } from "../../../../../generated/grpc/monitoreo_sesion_pb";
import { InformeSemanal } from "../../../../../modulos/monitoreo/sesion/informeSemanal";
import {
  LoteSesionDetalleResponse,
  PuntoSesionDetalleResponse,
  SesionDetalleResponse,
} from "../../../../../modulos/monitoreo/sesion/response/SesionDetalleResponse";
import {
  ESTADO_LABEL,
  ESTADO_RANK,
  evaluarEnLote,
  getAbreviatura,
  getEstadoLoteEvaluado,
  indexPlagasById,
  MonitoreoEstado,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { MESES } from "../../../../../utils/fechaUtils";
import { GOOGLE_MAPS_API_KEY } from "../../../../../../constantes";
import {
  COLOR_SIN_REFERENCIA,
  ESTADO_COLOR,
} from "../dashboard/monitoreoAnalisisPaleta";
import { buildSvgBarras, buildSvgDona } from "./reporteMonitoreoCharts";

/** Cuántas plagas entran en las barras agrupadas. Más allá de esto el grupo
 *  por lote se vuelve ilegible. */
const MAX_PLAGAS_EN_BARRAS = 6;


/** Opciones de armado del reporte. */
export type ReporteMonitoreoOptions = {
  /** `true` cuando el documento se muestra dentro de un `<iframe>` en la app
   *  (ruta `/monitoreo/plagas`): oculta su barra superior, porque la página
   *  contenedora ya aporta título, selector de sesión y botón de impresión.
   *  Por defecto `false` = documento suelto en su propia pestaña. */
  embebido?: boolean;
  /** `true` abre el diálogo de impresión apenas el documento termina de
   *  renderizar, para que el usuario elija ahí "Guardar como PDF" o una
   *  impresora. Solo para el documento suelto: dentro de un iframe imprimiría
   *  de forma impredecible según qué frame tenga el foco. */
  autoImprimir?: boolean;
  /** Contornos de los lotes de la finca (lat/lng), para dibujarlos bajo los
   *  árboles en el mapa de finca. Sin ellos el mapa se dibuja igual, solo que
   *  cuesta más ubicar cada punto. Los trae `cargarInformeSemanalAsync`. */
  poligonos?: { lat: number; lng: number }[][];
};

/**
 * Arma el reporte imprimible de una sesión como **documento HTML completo y
 * autocontenido**.
 *
 * Es la **única** implementación del reporte: la sirve tanto la pestaña
 * imprimible como el iframe de la ruta `/monitoreo/plagas`. Se eligió
 * documento suelto y no componentes de la app para que el `@media print`
 * controle la página entera sin tener que neutralizar el shell (sidebar,
 * topbar, overlays), que es donde la impresión desde un modal se rompe. El
 * precio es que no comparte nada con el bundle —ni CSS, ni ECharts— por eso
 * los estilos van embebidos y los gráficos son SVG estático.
 *
 * @param sesion  Informe de la semana a reportar (una o varias salidas ya
 *                fusionadas por `fusionarInformeSemanal`).
 * @param plagas  Catálogo de plagas y enfermedades.
 * @param options Ver `ReporteMonitoreoOptions`.
 */
export function buildReporteMonitoreoHtml(
  sesion: InformeSemanal,
  plagas: MonitoreoPlagaEnfermedadItem[],
  options: ReporteMonitoreoOptions = {},
): string {
  const plagasById = indexPlagasById(plagas);

  // El mismo documento sirve para una semana entera y para un solo recorrido,
  // así que el período no puede ir escrito a mano: se deduce del rango real
  // del informe. Si no, un informe de un día diría "Resumen de la semana" y
  // "Lotes monitoreados esta semana" — el mismo tipo de mentira que llevó a
  // fusionar la semana en primer lugar, ahora al revés.
  const periodo = sesion.fechaDesde === sesion.fechaHasta ? "el día" : "la semana";
  const periodoDemostrativo = periodo === "el día" ? "este día" : "esta semana";

  // Solo las plagas efectivamente detectadas en el período: el catálogo trae
  // las de todos los cultivos y las no detectadas serían columnas vacías.
  const plagasActivas = plagas
    .filter((plaga) => sesion.lotes.some((lote) => (lote.inc[plaga.id] ?? 0) > 0))
    .sort((a, b) => a.orden - b.orden);

  const lotesOrdenados = [...sesion.lotes].sort(
    (a, b) =>
      _rank(getEstadoLoteEvaluado(b, null, plagasById)) -
      _rank(getEstadoLoteEvaluado(a, null, plagasById)),
  );

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${_buildTitulo(sesion)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>${_buildEstilos()}</style>
</head>
<body class="${options.embebido ? "rp-embebido" : ""}">
${options.embebido ? "" : _buildTopbar(sesion)}
<div class="rp-page">
  ${_buildHeader(sesion)}
  <div class="rp-section-label">Resumen de ${periodo}</div>
  ${_buildKpis(plagasById, lotesOrdenados, periodoDemostrativo, sesion.puntos)}
  <div class="rp-section-label">Matriz de semáforo · lotes × plagas</div>
  ${_buildMatriz(lotesOrdenados, plagasActivas, plagasById, periodoDemostrativo)}
  <div class="rp-tabs no-print">
    <button class="rp-tab rp-tab--active" data-tab="lotes">Por lote</button>
    <button class="rp-tab" data-tab="plagas">Por plaga</button>
  </div>
  <div class="rp-panel rp-panel--active" id="tab-lotes">
    ${_buildTabLotes(sesion, lotesOrdenados, plagas, plagasById, options.poligonos ?? [])}
  </div>
  <div class="rp-panel" id="tab-plagas">
    ${_buildTabPlagas(sesion, lotesOrdenados, plagasActivas, options.poligonos ?? [])}
  </div>
  ${_buildFirma(sesion)}
  <div class="rp-footer">
    Generado por DATA AG · SEXTANTE — umbrales del catálogo de plagas y enfermedades
  </div>
</div>
<script>${_buildScript()}</script>
${options.autoImprimir ? `<script>${_buildAutoImprimirScript()}</script>` : ""}
</body>
</html>`;
}

/**
 * Título del documento. No es solo la etiqueta de la pestaña: al guardar desde
 * el diálogo de impresión, el navegador lo propone como **nombre del archivo**
 * PDF. Por eso lleva finca, semana y fecha — con la fecha los informes
 * guardados se ordenan solos y se distinguen entre años, cosa que la semana
 * ISO por sí sola no permite.
 *
 * Los separadores son `·` y `—`: ninguno está entre los caracteres que Windows
 * prohíbe en nombres de archivo (`\\ / : * ? " < > |`), así que sobreviven al
 * guardado sin que el navegador los reemplace.
 */
function _buildTitulo(sesion: SesionDetalleResponse): string {
  return `Informe de Monitoreo — ${sesion.fincaNombre} · Semana ${sesion.semana} · ${sesion.fecha.slice(0, 10)}`;
}

/** Cobertura del muestreo del lote: "22 de 120 árboles", o solo "22 árboles"
 *  si el lote no tiene cargado el total en la configuración de monitoreo
 *  (`totalArboles === 0`) — mostrar "de 0" sería peor que no decir nada. */
function _formatMuestreo(lote: LoteSesionDetalleResponse): string {
  return lote.totalArboles > 0
    ? `${lote.muestra} de ${lote.totalArboles} árboles`
    : `${lote.muestra} árboles`;
}

/** Fecha sin año ("24 de agosto"), para el extremo inicial de un rango: el
 *  año ya lo dice el extremo final. */
function _formatFechaCorta(isoDate: string): string {
  const [, mes, dia] = isoDate.slice(0, 10).split("-").map(Number);
  if (!mes || !dia) return isoDate;
  return `${dia} de ${MESES[mes - 1].toLowerCase()}`;
}

/** Fecha larga en español ("20 de agosto de 2026") a partir de `yyyy-MM-dd`. */
function _formatFechaLarga(isoDate: string): string {
  const [anio, mes, dia] = isoDate.slice(0, 10).split("-").map(Number);
  if (!anio || !mes || !dia) return isoDate;
  return `${dia} de ${MESES[mes - 1].toLowerCase()} de ${anio}`;
}

/** Barra superior con el botón de impresión (se oculta al imprimir). */
function _buildTopbar(sesion: SesionDetalleResponse): string {
  return `<div class="rp-topbar no-print">
    <span class="rp-brand">DATA<strong>AG</strong></span>
    <span class="rp-topbar-sep"></span>
    <span class="rp-breadcrumb">${sesion.fincaNombre} · Reporte de Monitoreo</span>
    <span class="rp-spacer"></span>
    <button class="rp-btn-print" onclick="Promise.resolve(window.__rpMapasListos).then(function(){ window.print(); })">Exportar PDF</button>
  </div>`;
}

/** Título, metadatos y badges del informe. */
function _buildHeader(sesion: InformeSemanal): string {
  // Una semana puede cubrirse en varias salidas: se informa el rango real y
  // cuántos recorridos entraron, para que no parezca que todo se midió el
  // mismo día.
  const fechas =
    sesion.fechaDesde && sesion.fechaDesde !== sesion.fechaHasta
      ? `${_formatFechaCorta(sesion.fechaDesde)} – ${_formatFechaLarga(sesion.fechaHasta)}`
      : _formatFechaLarga(sesion.fecha);

  const meta = [
    fechas,
    sesion.recorridos > 1 ? `${sesion.recorridos} recorridos` : null,
    sesion.monitor ? `Monitor: ${sesion.monitor}` : null,
    sesion.tipo || null,
    sesion.isFinalizada ? null : "Incluye recorridos sin finalizar",
  ].filter((dato): dato is string => !!dato);

  return `<div class="rp-header">
    <div>
      <h1 class="rp-title">Informe de Monitoreo — ${sesion.fincaNombre}</h1>
      <div class="rp-meta">${meta.map((dato) => `<span>${dato}</span>`).join("")}</div>
    </div>
    <div class="rp-badges">
      ${sesion.tipo ? `<span class="rp-badge">${sesion.tipo}</span>` : ""}
      <span class="rp-badge rp-badge--week">Semana ${sesion.semana}</span>
    </div>
  </div>`;
}

/** Tarjetas de KPI de la sesión. */
function _buildKpis(
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  lotes: LoteSesionDetalleResponse[],
  periodo: string,
  puntos: PuntoSesionDetalleResponse[],
): string {
  const porEstado = (estado: MonitoreoEstado) =>
    lotes.filter(
      (lote) => getEstadoLoteEvaluado(lote, null, plagasById) === estado,
    );

  const enAlerta = porEstado("alert");
  const enPrecaucion = porEstado("warn");
  const arboles = lotes.reduce((suma, lote) => suma + lote.muestra, 0);
  // Cobertura: solo se puede afirmar si TODOS los lotes tienen configurado su
  // total. Con uno solo sin cargar, el denominador saldría corto y el
  // porcentaje mentiría hacia arriba.
  const totalArboles = lotes.reduce((suma, lote) => suma + lote.totalArboles, 0);
  const coberturaCompleta = lotes.every((lote) => lote.totalArboles > 0);
  const subArboles =
    coberturaCompleta && totalArboles > 0
      ? `de ${totalArboles} árboles del cultivo`
      : "con registro GPS";

  // Mayor incidencia: el PEOR punto concreto de la sesión -qué plaga, en qué
  // lote y a qué porcentaje de árboles llegó-. Antes esta tarjeta sumaba
  // promedios por plaga entre lotes, que decía "cuál pesa más en total" pero no
  // dónde actuar; esto responde lo segundo, que es lo accionable.
  let peor: { incidencia: number; plaga: string; lote: string } | null = null;
  for (const lote of lotes) {
    const puntosDelLote = puntos.filter((punto) => punto.loteId === lote.id);
    for (const plagaId of Object.keys(lote.inc)) {
      const stats = _statsPorArbol(puntosDelLote, plagaId, lote.muestra);
      if (stats.afectados === 0) continue;
      if (peor === null || stats.incidencia > peor.incidencia) {
        peor = {
          incidencia: stats.incidencia,
          plaga: plagasById.get(plagaId)?.nombreComun ?? "—",
          lote: lote.nombre,
        };
      }
    }
  }

  const tarjetas = [
    { label: "Lotes monitoreados", valor: `${lotes.length}`, sub: periodo, estado: null },
    { label: "Árboles muestreados", valor: `${arboles}`, sub: subArboles, estado: null },
    {
      label: "Lotes en alerta",
      valor: `${enAlerta.length}`,
      sub: enAlerta.map((lote) => lote.nombre).join(", ") || "—",
      estado: enAlerta.length > 0 ? ("alert" as const) : null,
    },
    {
      label: "Lotes en precaución",
      valor: `${enPrecaucion.length}`,
      sub: enPrecaucion.map((lote) => lote.nombre).join(", ") || "—",
      estado: enPrecaucion.length > 0 ? ("warn" as const) : null,
    },
    {
      label: "Mayor incidencia",
      valor: peor ? `${peor.incidencia.toFixed(1)}%` : "—",
      sub: peor ? `${peor.plaga} · ${peor.lote}` : "sin detecciones",
      estado: null,
    },
  ];

  return `<div class="rp-kpis">${tarjetas
    .map(
      (tarjeta) => `<div class="rp-kpi${tarjeta.estado ? ` rp-kpi--${tarjeta.estado}` : ""}">
        <div class="rp-kpi-label">${tarjeta.label}</div>
        <div class="rp-kpi-value"${tarjeta.estado ? ` style="color:${ESTADO_COLOR[tarjeta.estado]}"` : ""}>${tarjeta.valor}</div>
        <div class="rp-kpi-sub">${tarjeta.sub}</div>
      </div>`,
    )
    .join("")}</div>`;
}

/** Matriz semáforo: una fila por lote, una columna por plaga detectada. */
function _buildMatriz(
  lotes: LoteSesionDetalleResponse[],
  plagasActivas: MonitoreoPlagaEnfermedadItem[],
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  periodo: string,
): string {
  if (plagasActivas.length === 0) {
    return `<p class="rp-vacio">Esta sesión no registró incidencias en ningún lote.</p>`;
  }

  // Encabezados abreviados: una columna por plaga detectada, y con el nombre
  // completo no entra ninguna. El nombre y el técnico quedan en el `title`.
  const encabezados = plagasActivas
    .map(
      (plaga) =>
        `<th title="${plaga.nombreComun} — ${plaga.nombreTecnico}"><span class="rp-mx-th">${getAbreviatura(plaga)}</span></th>`,
    )
    .join("");

  const filas = lotes
    .map((lote) => {
      const peor = getEstadoLoteEvaluado(lote, null, plagasById);
      const celdas = plagasActivas
        .map((plaga) => {
          const valor = lote.inc[plaga.id] ?? 0;
          if (valor === 0) return `<td><span class="rp-mx-cell rp-mx-cell--cero">·</span></td>`;
          const evaluacion = evaluarEnLote(lote, plaga.id, plaga);
          if (!evaluacion || evaluacion.estado === null) {
            // Hay número pero no hay con qué evaluarlo: se muestra sin color, y
            // el título lo dice en vez de dejar al lector deducirlo del gris.
            return `<td><span class="rp-mx-cell" title="Sin valores de referencia">${formatPorcentaje(evaluacion?.incidencia ?? 0)}</span></td>`;
          }
          return `<td><span class="rp-mx-cell rp-mx-cell--${evaluacion.estado}" title="${ESTADO_LABEL[evaluacion.estado]}">${formatPorcentaje(evaluacion.incidencia)}</span></td>`;
        })
        .join("");
      return `<tr>
        <td class="rp-mx-lote">
          <span class="rp-mx-lote-nombre"><span class="rp-dot" style="background:${peor === null ? COLOR_SIN_REFERENCIA : ESTADO_COLOR[peor]}"></span>${lote.nombre}</span>
          <span class="rp-mx-lote-meta">${lote.areaHa.toFixed(1)} ha · ${_formatMuestreo(lote)}</span>
        </td>
        ${celdas}
      </tr>`;
    })
    .join("");

  // La leyenda es obligatoria, no decorativa: en la matriz el número es el
  // valor y el **fondo de la celda** es el único portador del nivel. En papel
  // no hay tooltip que lo aclare, así que sin esto el PDF sale con colores sin
  // explicar.
  const leyenda = (["alert", "warn", "ok"] as const)
    .map(
      (estado) =>
        `<span class="rp-mx-leyenda-item"><span class="rp-mx-cell rp-mx-cell--${estado}">&nbsp;</span>${ESTADO_LABEL[estado]}</span>`,
    )
    .join("");

  return `<div class="rp-matriz">
    <div class="rp-mx-header">
      <div>
        <div class="rp-mx-title">Estado por lote y plaga</div>
        <div class="rp-mx-sub">Solo plagas con incidencia registrada ${periodo}</div>
      </div>
      <div class="rp-mx-leyenda">${leyenda}</div>
    </div>
    <div class="rp-mx-scroll">
      <table class="rp-mx-table">
        <thead><tr><th class="rp-mx-lote">Lote</th>${encabezados}</tr></thead>
        <tbody>${filas}</tbody>
      </table>
    </div>
  </div>`;
}


/** Sentinela de GPS sin fijación: la app móvil lo manda cuando no consiguió
 *  coordenada real (`ubicacion ?? {type: Point, coordinates: [0,0]}` en
 *  `SyncService.buildPayload`). Cae en medio del Atlántico — incluir ese
 *  punto arruinaría el auto-encuadre de Google, que terminaría alejando la
 *  vista hasta abarcar Colombia y el Atlántico a la vez. */
function _esPuntoSinFijacion(punto: PuntoSesionDetalleResponse): boolean {
  return punto.lat === 0 && punto.lng === 0;
}


/** Datos de un mini-mapa de recorrido: puntos ya resueltos a color y
 *  segmentados por día, listos para que el script del documento los
 *  dibuje con Leaflet. Datos crudos y no una URL de imagen: el reporte no
 *  depende de ningún servicio de mapas ESTÁTICOS de terceros -la Google
 *  Maps Static API resultó no estar habilitada para la key existente-,
 *  solo de los mismos tiles `vt` que ya usa el mapa base del resto de la
 *  app. */
type DatosMapaLote = {
  /** Un array de puntos por día calendario -ver el porqué en el docblock
   *  de `_buildDatosMapaLote`-, ya sin el sentinela GPS y con al menos 2
   *  puntos (con uno solo no hay nada que conectar). */
  segmentos: { lat: number; lng: number }[][];
  /** Un marcador por árbol válido, con el color hex de su peor plaga ya
   *  resuelto (`getPeorPlagaLote`/`ESTADO_COLOR` — el mismo semáforo
   *  que colorea lotes, sin duplicar la regla) y el texto de su tooltip
   *  ya armado -"Sin detección" si no tuvo ninguna-. */
  marcadores: {
    lat: number;
    lng: number;
    color: string;
    numeroArbol: number;
    etiqueta: string;
  }[];
  /** Contornos de lote a dibujar debajo (solo el mapa de finca los usa). */
  poligonos?: { lat: number; lng: number }[][];
  /** Margen del encuadre en píxeles. El mapa de finca pide un poco más, para que
   *  se vea la finca y no solo el recorrido. */
  padding?: number;
  /** `true` usa satélite SIN etiquetas. El mapa de finca va alejado, y a ese zoom
   *  la capa híbrida se llena de nombres de negocios y vías que tapan los
   *  árboles. */
  sinEtiquetas?: boolean;
};

/**
 * Arma los datos del mapa de recorrido: polilíneas por día y un marcador por
 * árbol, coloreado con el semáforo de su peor plaga.
 *
 * Hoy lo usa el **mapa de finca**, con todos los puntos de la sesión. Hubo
 * también un mapa dentro de cada tarjeta de lote; se quitó (2026-10-13) porque
 * repetía el mismo recorrido en pedazos y alargaba el informe impreso.
 *
 * `null` si no hay puntos utilizables (sesión sin recorrido, o todos sus puntos
 * con el sentinela de GPS sin fijación).
 *
 * Segmentado por día y no un solo trazo continuo: el informe puede fusionar
 * varias salidas de la semana (`fusionarInformeSemanal`), y conectar el último
 * árbol del lunes con el primero del miércoles dibujaría un camino que el
 * monitor nunca caminó.
 */
function _buildDatosMapaLote(
  puntos: PuntoSesionDetalleResponse[],
  lotes: LoteSesionDetalleResponse[],
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
): DatosMapaLote | null {
  const validos = puntos.filter((p) => !_esPuntoSinFijacion(p));
  if (validos.length === 0) return null;

  const porDia = new Map<string, PuntoSesionDetalleResponse[]>();
  for (const punto of validos) {
    const dia = punto.fechaCaptura.slice(0, 10);
    const lista = porDia.get(dia) ?? [];
    lista.push(punto);
    porDia.set(dia, lista);
  }
  const segmentos = [...porDia.values()]
    .filter((puntosDia) => puntosDia.length >= 2) // nada que conectar con un solo punto
    .map((puntosDia) => puntosDia.map((p) => ({ lat: p.lat, lng: p.lng })));

  const marcadores = validos.map((punto) => {
    const peor = _peorEnArbol(punto.detalles, lotes, plagasById);
    return {
      lat: punto.lat,
      lng: punto.lng,
      color: peor ? ESTADO_COLOR[peor.estado] : ESTADO_COLOR.ok,
      numeroArbol: punto.numeroArbol,
      etiqueta: peor
        ? `${peor.plaga.nombreComun} · ${formatValor(peor.nivel)}`
        : "Sin detección",
    };
  });

  return { segmentos, marcadores };
}

/**
 * La referencia congelada de un organismo en esta sesion.
 *
 * Se busca lote por lote y se toma la primera: dentro de una sesion los lotes del
 * mismo cultivo comparten referencia, y es lo que necesitan los bloques que
 * describen al organismo en TODA la sesion (su encabezado, la escala del mapa).
 * Los bloques que describen a UN lote usan `lote.referencias[plagaId]` directo,
 * que es exacto -una finca con limon y naranja puede tener dos referencias
 * distintas para el mismo acaro-.
 */
function _referenciaDe(
  lotes: LoteSesionDetalleResponse[],
  plagaId: string,
): MonitoreoReferencia | null {
  for (const lote of lotes) {
    const referencia = lote.referencias[plagaId];
    if (referencia) return referencia;
  }
  return null;
}

/** Peso para ordenar, con lo no evaluable al fondo: un lote sin referencias no es
 *  menos grave que uno en control, es desconocido, y encabezar el informe con el
 *  taparia los que si hay que ir a ver. */
function _rank(estado: MonitoreoEstado | null): number {
  return estado === null ? -1 : ESTADO_RANK[estado];
}

/**
 * El organismo que define el color del marcador de UN arbol.
 *
 * Evalua solo el **nivel**, nunca la incidencia: un arbol no tiene porcentaje de
 * arboles afectados, es uno. Con `muestra = 1` la incidencia daria 0% o 100%, y
 * con cualquier umbral de incidencia por debajo de 100 todo arbol con una sola
 * deteccion saldria en alerta -el mapa del informe quedaria todo rojo-.
 */
function _peorEnArbol(
  detalles: Record<string, number>,
  lotes: LoteSesionDetalleResponse[],
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
): { plaga: MonitoreoPlagaEnfermedadItem; nivel: number; estado: MonitoreoEstado } | null {
  let peor: { plaga: MonitoreoPlagaEnfermedadItem; nivel: number; estado: MonitoreoEstado } | null = null;

  for (const [plagaId, valor] of Object.entries(detalles)) {
    if (valor <= 0) continue;
    const plaga = plagasById.get(plagaId);
    if (!plaga) continue;

    const referencia = _referenciaDe(lotes, plagaId);
    if (referencia?.grupoId === GRUPO_BENEFICO) continue;

    const nivel =
      calcularNivel({ promedio: valor, afectados: 1, muestra: 1, referencia }) ?? valor;
    const estado =
      referencia?.esCritico === true
        ? "alert"
        : estadoDeUnValor(nivel, referencia?.nivelBajoMax, referencia?.nivelMedioMax);
    if (estado === null) continue;

    if (peor === null || ESTADO_RANK[estado] > ESTADO_RANK[peor.estado] || (ESTADO_RANK[estado] === ESTADO_RANK[peor.estado] && nivel > peor.nivel)) {
      peor = { plaga, nivel, estado };
    }
  }

  return peor;
}

/**
 * Cuántos árboles del lote tienen la plaga y cuál es el peor de todos.
 *
 * Sale de los conteos CRUDOS por árbol (`sesion.puntos[].detalles`), que ya
 * viajan en el detalle: el server solo manda el promedio del lote (`lote.inc`),
 * y un promedio no distingue "poquito en todos" de "un foco en un árbol".
 *
 * La incidencia que devuelve es **la misma métrica que mide el semáforo** desde
 * que existe el motor de evaluación: `afectados / muestra × 100`. Antes no lo
 * era —el semáforo comparaba el promedio de conteo contra umbrales de nivel— y
 * esta función llevaba una advertencia sobre esas dos escalas conviviendo.
 *
 * Sigue calculándose acá y no se toma del servidor porque además devuelve
 * `maximo`, el peor árbol del lote, que el servidor no manda: un promedio no
 * distingue "poquito en todos" de "un foco en un árbol".
 */
function _statsPorArbol(
  puntosDelLote: PuntoSesionDetalleResponse[],
  plagaId: string,
  muestra: number,
): { afectados: number; total: number; incidencia: number; maximo: number } {
  let afectados = 0;
  let maximo = 0;
  for (const punto of puntosDelLote) {
    const valor = punto.detalles[plagaId] ?? 0;
    if (valor > 0) {
      afectados++;
      if (valor > maximo) maximo = valor;
    }
  }
  // Denominador: los árboles que el server reporta como muestreados. Se cae a
  // los puntos recibidos solo si viniera en 0, para no dividir por cero.
  const total = muestra > 0 ? muestra : puntosDelLote.length;
  return {
    afectados,
    total,
    incidencia: total > 0 ? (afectados / total) * 100 : 0,
    maximo,
  };
}

/** Panel "Por lote": una tarjeta por lote con fenología, observaciones y sus
 *  plagas detectadas contra el umbral. */
function _buildTabLotes(
  sesion: SesionDetalleResponse,
  lotes: LoteSesionDetalleResponse[],
  plagas: MonitoreoPlagaEnfermedadItem[],
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  poligonos: { lat: number; lng: number }[][],
): string {
  return _mapaFincaHtml(sesion, plagasById, poligonos) + lotes
    .map((lote) => {
      // Los puntos del lote ya no dibujan un mapa propio -el de finca los cubre
      // todos-, pero siguen haciendo falta para los estadisticos por arbol
      // (afectados, incidencia y maximo) de cada tarjeta de plaga.
      const puntosDelLote = sesion.puntos.filter((punto) => punto.loteId === lote.id);
      const peor = getEstadoLoteEvaluado(lote, null, plagasById);

      const detectadas = plagas
        .map((plaga) => ({
          plaga,
          valor: lote.inc[plaga.id] ?? 0,
          evaluacion: evaluarEnLote(lote, plaga.id, plaga),
        }))
        .filter((entrada) => entrada.valor > 0 && entrada.evaluacion !== null)
        .sort(
          (a, b) =>
            _rank(b.evaluacion!.estado) - _rank(a.evaluacion!.estado) ||
            b.evaluacion!.incidencia - a.evaluacion!.incidencia,
        );

      const tarjetas = detectadas
        .map(({ plaga, evaluacion: ev }) => {
          const evaluacion = ev!;
          const estado = evaluacion.estado;
          const color = estado === null ? COLOR_SIN_REFERENCIA : ESTADO_COLOR[estado];
          const etiqueta =
            estado === null ? "Sin valores de referencia" : ESTADO_LABEL[estado];
          const referencia = lote.referencias[plaga.id] ?? null;

          // La barra mide la INCIDENCIA contra el umbral de incidencia, que es la
          // misma escala del número grande de la tarjeta. Antes medía el promedio
          // por árbol contra un umbral de nivel: dos escalas en una tarjeta, así
          // que la barra y el porcentaje podían contar historias distintas.
          const incMedio = referencia?.incidenciaMedioMax;
          const incBajo = referencia?.incidenciaBajoMax;
          const tope =
            incMedio && incMedio > 0
              ? incMedio
              : Math.max(evaluacion.incidencia, 1);
          const porcentaje = Math.min((evaluacion.incidencia / tope) * 100, 100);
          const marcaBaja =
            incBajo && incBajo > 0 && incMedio && incMedio > 0
              ? (incBajo / tope) * 100
              : null;

          const stats = _statsPorArbol(puntosDelLote, plaga.id, lote.muestra);
          return `<div class="rp-plaga rp-plaga--${estado ?? "sin-ref"}">
            <div class="rp-plaga-top">
              <span class="rp-plaga-nombre">${plaga.nombreComun}</span>
              <span class="rp-plaga-badge" style="color:${color}">${etiqueta}</span>
            </div>
            <div class="rp-plaga-valor">${formatPorcentaje(evaluacion.incidencia)}</div>
            <div class="rp-plaga-sub">${stats.afectados} de ${stats.total} árboles afectados</div>
            <div class="rp-plaga-tecnico">${plaga.nombreTecnico}</div>
            <div class="rp-barra">
              <div class="rp-barra-fill" style="width:${porcentaje.toFixed(1)}%;background:${color}"></div>
              ${marcaBaja !== null ? `<span class="rp-barra-marca" style="left:${marcaBaja.toFixed(1)}%"></span>` : ""}
            </div>
            <div class="rp-barra-labels">
              <span>0</span>
              <span>Precaución: ${incBajo ? formatPorcentaje(incBajo) : "—"}</span>
              <span>Alerta: ${incMedio ? formatPorcentaje(incMedio) : "—"}</span>
            </div>
            <div class="rp-plaga-stats">
              <span><b>${formatNivel(evaluacion)}</b> nivel</span>
              <span><b>${formatValor(stats.maximo)}</b> máx. árbol</span>
              <span><b>${incMedio ? formatPorcentaje(incMedio) : "—"}</b> umbral acción</span>
            </div>
          </div>`;
        })
        .join("");

      return `<div class="rp-lote rp-lote--${peor}">
        <div class="rp-lote-header">
          <div>
            <div class="rp-lote-nombre">${lote.nombre}</div>
            <div class="rp-lote-meta">${lote.areaHa.toFixed(1)} ha · ${_formatMuestreo(lote)} muestreados</div>
          </div>
          <span class="rp-lote-badge" style="background:${peor === null ? COLOR_SIN_REFERENCIA : ESTADO_COLOR[peor]}">${peor === null ? "Sin valores de referencia" : ESTADO_LABEL[peor]}</span>
        </div>
        <div class="rp-lote-body">
          ${_fenologiaHtml(lote)}
          ${lote.observaciones ? `<p class="rp-obs">${lote.observaciones}</p>` : ""}
          ${
            detectadas.length > 0
              ? `<div class="rp-section-label rp-section-label--sm">Incidencias registradas</div><div class="rp-plagas-grid">${tarjetas}</div>`
              : `<p class="rp-vacio">Sin incidencia registrada en este lote.</p>`
          }
        </div>
      </div>`;
    })
    .join("");
}

/**
 * Mapa de TODA la finca, al inicio del panel: un punto por árbol muestreado,
 * coloreado con el MISMO semáforo de plagas que los mapas de lote, y el
 * recorrido de la sesión por encima.
 *
 * El color es la plaga y no el lote: así significa lo mismo en los dos mapas del
 * informe, y es lo que de verdad se quiere leer de un vistazo -dónde se está
 * disparando algo-. Los lotes se distinguen por su posición y por el mapa de su
 * propia tarjeta, no por color.
 *
 * Convive con los mapas por lote y no los reemplaza: éste responde "cómo se
 * repartió la presión en la finca entera", que un mapa por tarjeta no puede
 * mostrar; el de cada lote sigue sirviendo para el detalle de su recorrido.
 */
function _mapaFincaHtml(
  sesion: SesionDetalleResponse,
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  poligonos: { lat: number; lng: number }[][],
): string {
  // Mismos datos que un mapa de lote, pero con TODOS los puntos: reusa el corte
  // por día y el coloreado por peor plaga, sin una segunda copia de esa regla.
  const datos = _buildDatosMapaLote(sesion.puntos, sesion.lotes, plagasById);
  if (datos === null) return "";
  // Los contornos entran al encuadre, así que el mapa muestra la finca entera y
  // no solo el trazo del recorrido -que es lo que lo dejaba "pegado" al camino-.
  datos.poligonos = poligonos;
  datos.padding = 16;
  datos.sinEtiquetas = true;

  const leyenda = (["ok", "warn", "alert"] as const)
    .map(
      (estado) =>
        `<span class="rp-feno-item"><span class="rp-feno-punto" style="background:${ESTADO_COLOR[estado]}"></span>${ESTADO_LABEL[estado]}</span>`,
    )
    .join("");

  return `<div class="rp-mapa-finca">
    <div class="rp-section-label">Distribución espacial de árboles monitoreados · ${sesion.fincaNombre}</div>
    <div class="rp-mapa-finca-wrap">
      <div class="rp-lote-mapa" data-mapa='${JSON.stringify(datos)}'></div>
      <span class="rp-mapa-finca-pie">${sesion.fincaNombre} · ${_formatFechaLarga(sesion.fecha)}</span>
    </div>
    <div class="rp-feno-leyenda rp-mapa-finca-leyenda">${leyenda}</div>
  </div>`;
}

/** Color fijo por estado fenológico: el mismo estado se ve igual en todos los
 *  lotes y entre informes, que es lo que permite compararlos de un vistazo.
 *  Van por nombre y no por posición, porque el orden cambia según qué estados
 *  tenga cada lote. */
const FENO_COLOR: Record<string, string> = {
  "Estado Vegetativo": "#2f9e44",
  "Floración": "#f08c00",
  "Cuajado": "#1971c2",
  "Fructificación": "#c2255c",
};
const FENO_COLOR_OTRO = "#868e96";

/**
 * Estado fenológico del lote como barra apilada: un segmento por estado, del
 * ancho de su porcentaje de árboles, con la leyenda debajo.
 *
 * El porcentaje se calcula sobre la SUMA de la distribución y no sobre
 * `lote.muestra`: si algún árbol quedara sin estado registrado, los segmentos
 * seguirían sumando 100 y la barra no mostraría un hueco sin explicación.
 *
 * Si el server no mandó distribución -sesiones viejas-, se cae al valor único
 * `fenologia`, que es lo que mostraba el informe antes.
 */
function _fenologiaHtml(lote: LoteSesionDetalleResponse): string {
  const dist = (lote.fenologiaDistribucion ?? []).filter((f) => f.arboles > 0);
  const total = dist.reduce((suma, f) => suma + f.arboles, 0);

  if (total === 0) {
    return lote.fenologia
      ? `<div class="rp-feno"><span class="rp-feno-label">Estado fenológico</span><span class="rp-feno-valor">${lote.fenologia}</span></div>`
      : "";
  }

  const partes = dist.map((f) => ({
    ...f,
    pct: (f.arboles / total) * 100,
    color: FENO_COLOR[f.estado] ?? FENO_COLOR_OTRO,
  }));

  return `<div class="rp-feno-bloque">
    <div class="rp-feno-label">Estado fenológico del lote</div>
    <div class="rp-feno-barra">
      ${partes
        .map(
          (f) =>
            `<div class="rp-feno-seg" style="width:${f.pct.toFixed(2)}%;background:${f.color}" title="${f.estado}: ${f.arboles} árbol(es)"></div>`,
        )
        .join("")}
    </div>
    <div class="rp-feno-leyenda">
      ${partes
        .map(
          (f) =>
            `<span class="rp-feno-item"><span class="rp-feno-punto" style="background:${f.color}"></span>${f.estado} ${f.pct.toFixed(1)}%</span>`,
        )
        .join("")}
    </div>
  </div>`;
}

/** Panel "Por plaga": los dos gráficos y, debajo, cada plaga contra todos los
 *  lotes. */
function _buildTabPlagas(
  sesion: SesionDetalleResponse,
  lotes: LoteSesionDetalleResponse[],
  plagasActivas: MonitoreoPlagaEnfermedadItem[],
  poligonos: { lat: number; lng: number }[][],
): string {
  if (plagasActivas.length === 0) {
    return `<p class="rp-vacio">Esta sesión no registró incidencias.</p>`;
  }

  const conteos: Record<MonitoreoEstado, number> = { alert: 0, warn: 0, ok: 0 };
  for (const lote of sesion.lotes) {
    for (const plaga of plagasActivas) {
      if ((lote.inc[plaga.id] ?? 0) === 0) continue;
      const estado = evaluarEnLote(lote, plaga.id, plaga)?.estado;
      // Lo no evaluable no entra en la dona: sumarlo a un tramo diría que se
      // clasificó, y el total de la dona dejaría de ser "lo que se clasificó".
      if (estado) conteos[estado] += 1;
    }
  }

  const graficos = `<div class="rp-charts">
    <div class="rp-chart-card">
      <div class="rp-chart-title">Incidencia por lote</div>
      <div class="rp-chart-sub">Conteo promedio por árbol · plagas con mayor presencia</div>
      ${buildSvgBarras(lotes, plagasActivas.slice(0, MAX_PLAGAS_EN_BARRAS))}
    </div>
    <div class="rp-chart-card">
      <div class="rp-chart-title">Distribución de alertas</div>
      <div class="rp-chart-sub">Combinaciones lote × plaga por nivel</div>
      ${buildSvgDona(conteos)}
    </div>
  </div>`;

  const secciones = plagasActivas
    .map((plaga) => {
      const porLote = sesion.lotes
        .map((lote) => ({
          lote,
          evaluacion: evaluarEnLote(lote, plaga.id, plaga),
        }))
        .filter((entrada) => entrada.evaluacion !== null)
        .sort(
          (a, b) =>
            _rank(b.evaluacion!.estado) - _rank(a.evaluacion!.estado) ||
            b.evaluacion!.incidencia - a.evaluacion!.incidencia,
        );

      const peor = porLote.reduce<MonitoreoEstado | null>(
        (acumulado, entrada) =>
          _rank(entrada.evaluacion!.estado) > _rank(acumulado)
            ? entrada.evaluacion!.estado
            : acumulado,
        null,
      );

      const tarjetas = porLote
        .map(({ lote, evaluacion: ev }) => {
          const evaluacion = ev!;
          const estado = evaluacion.estado;
          const color = estado === null ? COLOR_SIN_REFERENCIA : ESTADO_COLOR[estado];
          const referencia = lote.referencias[plaga.id] ?? null;
          const incBajo = referencia?.incidenciaBajoMax;
          const incMedio = referencia?.incidenciaMedioMax;
          const tope =
            incMedio && incMedio > 0
              ? incMedio
              : Math.max(evaluacion.incidencia, 1);
          const porcentaje = Math.min((evaluacion.incidencia / tope) * 100, 100);
          return `<div class="rp-bp">
            <div class="rp-bp-lote">${lote.nombre}</div>
            <div class="rp-bp-valor" style="color:${color}">${formatPorcentaje(evaluacion.incidencia)}</div>
            <div class="rp-bp-sub">Precaución: ${incBajo ? formatPorcentaje(incBajo) : "—"} · alerta: ${incMedio ? formatPorcentaje(incMedio) : "—"}</div>
            <div class="rp-barra"><div class="rp-barra-fill" style="width:${porcentaje.toFixed(1)}%;background:${color}"></div></div>
          </div>`;
        })
        .join("");

      return `<div class="rp-bp-section">
        <div class="rp-bp-header">
          <div>
            <div class="rp-bp-title">${plaga.nombreComun}</div>
            <div class="rp-bp-tecnico">${plaga.nombreTecnico}</div>
          </div>
          <span class="rp-lote-badge" style="background:${peor === null ? COLOR_SIN_REFERENCIA : ESTADO_COLOR[peor]}">${peor === null ? "Sin valores de referencia" : ESTADO_LABEL[peor]}</span>
        </div>
        <div class="rp-bp-grid">${tarjetas}</div>
        ${peor === "ok" ? "" : _mapaPorPlagaHtml(sesion, plaga, poligonos)}
      </div>`;
    })
    .join("");

  return graficos + secciones;
}

/**
 * Rampa de color por CONTEO de una plaga, anclada a sus propios umbrales.
 *
 * No es una escala cruda 0→máximo: esa no se puede comparar entre plagas, porque
 * 20 ácaros y 20 moscas blancas no significan lo mismo. Al cortar por el umbral
 * de cada plaga, el color sigue diciendo "respecto al umbral" -lo mismo que en
 * el resto del informe- y además muestra magnitud dentro de cada tramo.
 *
 * Sin umbral de alerta definido (`rangoMedio` en 0) se reparte contra el máximo
 * observado, que es lo único disponible; la leyenda lo dice.
 */
function _tramosDeConteo(
  referencia: MonitoreoReferencia | null,
  maximo: number,
): { desde: number; color: string; etiqueta: string }[] {
  // Esta escala colorea ÁRBOLES por su conteo, así que va contra los umbrales de
  // **nivel** y no de incidencia: un árbol no tiene porcentaje de árboles
  // afectados. Es la otra escala legítima del informe, y por eso su leyenda se
  // titula "Conteo por árbol".
  const nivelMedio = referencia?.nivelMedioMax;
  const nivelBajo = referencia?.nivelBajoMax;
  const alerta = nivelMedio && nivelMedio > 0 ? nivelMedio : Math.max(maximo, 1);
  const bajo = nivelBajo && nivelBajo > 0 ? nivelBajo : alerta / 2;
  const fmt = (n: number) => formatValor(n);
  return [
    { desde: 0, color: "#ced4da", etiqueta: "0 · sin detección" },
    { desde: 0.0001, color: "#a1d99b", etiqueta: `menos de ${fmt(bajo)}` },
    { desde: bajo, color: "#fed976", etiqueta: `${fmt(bajo)} a ${fmt(alerta)}` },
    { desde: alerta, color: "#fd8d3c", etiqueta: `${fmt(alerta)} a ${fmt(alerta * 2)}` },
    { desde: alerta * 2, color: "#e31a1c", etiqueta: `más de ${fmt(alerta * 2)}` },
  ];
}

/**
 * Mapa de la finca coloreado por el conteo de UNA plaga, con su leyenda al lado.
 *
 * Solo se dibuja para las plagas que superan umbral en algún lote: con ~20 plagas
 * detectadas por sesión, uno por cada una serían 20 mapas Leaflet en un documento
 * imprimible -20 pedidos de tiles y 20 capturas al imprimir-. Y en una plaga con
 * 0.04 de promedio el mapa no cambia ninguna decisión.
 *
 * Los árboles SIN la plaga van en gris, no se ocultan: un mapa con tres puntos
 * rojos y nada más se leería como "casi no se monitoreó", cuando se revisaron
 * todos.
 */
function _mapaPorPlagaHtml(
  sesion: SesionDetalleResponse,
  plaga: MonitoreoPlagaEnfermedadItem,
  poligonos: { lat: number; lng: number }[][],
): string {
  const validos = sesion.puntos.filter((punto) => !_esPuntoSinFijacion(punto));
  if (validos.length === 0) return "";

  const maximo = validos.reduce(
    (peor, punto) => Math.max(peor, punto.detalles[plaga.id] ?? 0),
    0,
  );
  const referenciaDeLaPlaga = _referenciaDe(sesion.lotes, plaga.id);
  const tramos = _tramosDeConteo(referenciaDeLaPlaga, maximo);
  const colorDe = (valor: number): string =>
    [...tramos].reverse().find((tramo) => valor >= tramo.desde)?.color ?? tramos[0].color;

  const datos: DatosMapaLote = {
    // Sin polilínea: acá interesa dónde está la plaga, no en qué orden se caminó
    // -el recorrido ya se ve en el mapa de arriba-.
    segmentos: [],
    marcadores: validos.map((punto) => {
      const valor = punto.detalles[plaga.id] ?? 0;
      return {
        lat: punto.lat,
        lng: punto.lng,
        color: colorDe(valor),
        numeroArbol: punto.numeroArbol,
        etiqueta: `${plaga.nombreComun} · ${formatValor(valor)}`,
      };
    }),
    poligonos,
    padding: 16,
    sinEtiquetas: true,
  };

  const leyenda = tramos
    .map(
      (tramo) =>
        `<span class="rp-escala-item"><span class="rp-feno-punto" style="background:${tramo.color}"></span>${tramo.etiqueta}</span>`,
    )
    .join("");

  return `<div class="rp-bp-mapa">
    <div class="rp-lote-mapa" data-mapa='${JSON.stringify(datos)}'></div>
    <div class="rp-escala">
      <div class="rp-escala-titulo">Conteo por árbol</div>
      ${leyenda}
      ${referenciaDeLaPlaga?.nivelMedioMax ? "" : `<div class="rp-escala-nota">Sin umbral definido: tramos sobre el máximo observado.</div>`}
    </div>
  </div>`;
}

/** Bloque de recomendaciones y firma: los tres campos que el agrónomo llena en
 *  pantalla antes de imprimir. */
function _buildFirma(sesion: SesionDetalleResponse): string {
  return `<div class="rp-firma">
    <div class="rp-firma-title">Recomendaciones y firma</div>
    <div class="rp-firma-body">
      <div>
        <div class="rp-firma-label">Observaciones generales del monitor</div>
        <textarea class="rp-textarea" rows="4" placeholder="Observaciones adicionales del recorrido…"></textarea>
      </div>
      <div>
        <div class="rp-firma-label">Recomendaciones del agrónomo</div>
        <textarea class="rp-textarea" rows="4" placeholder="Recomendaciones de manejo y aplicaciones…"></textarea>
      </div>
      <div>
        <div class="rp-firma-label">Próxima visita</div>
        <input type="date" class="rp-input-fecha">
        <div class="rp-firma-linea"></div>
        <div class="rp-firma-label">Agrónomo responsable</div>
        <div class="rp-firma-agronomo">${sesion.agronomo ?? "—"}</div>
      </div>
    </div>
  </div>`;
}

/** Script del documento: tabs y carga de la imagen del mapa. Es el único JS
 *  del reporte — todo lo demás ya viene renderizado. */
function _buildScript(): string {
  return `
(function () {
  document.querySelectorAll('.rp-tab').forEach(function (boton) {
    boton.addEventListener('click', function () {
      var destino = boton.dataset.tab;
      document.querySelectorAll('.rp-tab').forEach(function (otro) {
        otro.classList.toggle('rp-tab--active', otro === boton);
      });
      document.querySelectorAll('.rp-panel').forEach(function (panel) {
        panel.classList.toggle('rp-panel--active', panel.id === 'tab-' + destino);
      });
      // El panel recién mostrado ya tiene alto: recién ahora Leaflet puede medir
      // sus mapas. Sin esto, los del panel "Por plaga" se quedan en gris.
      mapasInteractivos.forEach(function (mapa) {
        mapa.invalidateSize();
        if (mapa.__rpEncuadre) {
          mapa.fitBounds(mapa.__rpEncuadre, { padding: [mapa.__rpMargen, mapa.__rpMargen] });
        }
      });
    });
  });

  // Mini-mapas de recorrido por lote: tiles Google \`vt\` crudos -los mismos
  // que ya usa el mapa base del resto de la app, sin depender de la Maps
  // Static API (key aparte, con su propia habilitacion de producto)-.
  var URL_TILES = 'https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}';
  // 's' = satelite puro (sin nombres de calles ni negocios); 'y' = hibrido.
  var URL_TILES_LIMPIO = 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}';
  var OPCIONES_TILES = { subdomains: ['mt0', 'mt1', 'mt2', 'mt3'], maxZoom: 20 };

  // Agrega la capa de tiles + el recorrido (polilineas y marcadores) a un
  // mapa Leaflet ya creado. Comun a los dos mapas de cada lote -el
  // interactivo y el oculto de \`crearMapaOculto\`-, para no duplicar la
  // logica de dibujado entre ambos.
  function agregarRecorrido(mapa, datos, conTooltip) {
    var tiles = L.tileLayer(datos.sinEtiquetas ? URL_TILES_LIMPIO : URL_TILES, OPCIONES_TILES).addTo(mapa);
    var limites = [];
    // Contornos de los lotes, por debajo de todo: dan referencia de dónde cae
    // cada árbol. Sin relleno, para no apagar la imagen satelital.
    (datos.poligonos || []).forEach(function (anillo) {
      var latlngs = anillo.map(function (p) { return [p.lat, p.lng]; });
      L.polygon(latlngs, { color: '#e03131', weight: 1.5, opacity: 0.9, fill: false }).addTo(mapa);
      limites = limites.concat(latlngs);
    });
    datos.segmentos.forEach(function (segmento) {
      L.polyline(
        segmento.map(function (p) { return [p.lat, p.lng]; }),
        { color: '#1E88E5', weight: 3, opacity: 0.85 },
      ).addTo(mapa);
    });
    var puntosLatLng = [];
    datos.marcadores.forEach(function (m) {
      var marcador = L.circleMarker([m.lat, m.lng], {
        radius: 6, color: '#fff', weight: 1.5, fillColor: m.color, fillOpacity: 1,
      }).addTo(mapa);
      if (conTooltip) marcador.bindTooltip('Árbol ' + m.numeroArbol + ' · ' + m.etiqueta);
      puntosLatLng.push([m.lat, m.lng]);
    });
    var margen = datos.padding || 12;
    // Encuadre: manda la FINCA (sus contornos), no los árboles. Un solo punto con
    // GPS lejano -visto en datos reales: árboles capturados a 190 km de la finca-
    // obligaba al mapa a abarcar los dos y se veía media región sin nada
    // distinguible. Los árboles que caen dentro de la finca (con 30% de holgura)
    // sí amplían el encuadre; los de más allá se dibujan igual, pero no lo
    // arrastran. Sin contornos no hay con qué acotar y se usan los puntos.
    var encuadre = puntosLatLng;
    if (limites.length > 0) {
      var caja = L.latLngBounds(limites);
      var holgura = caja.pad(0.3);
      encuadre = limites.concat(puntosLatLng.filter(function (p) {
        return holgura.contains(L.latLng(p[0], p[1]));
      }));
    }
    mapa.fitBounds(encuadre, { padding: [margen, margen] });
    // Se guarda el encuadre en el propio mapa: los que nacen en una pestaña
    // oculta lo calculan sobre un contenedor de alto 0, asi que hay que
    // rehacerlo cuando la pestaña se muestra (ver el manejador de tabs).
    mapa.__rpEncuadre = encuadre;
    mapa.__rpMargen = margen;
    return tiles;
  }

  // Mapa OCULTO de tamaño FIJO (el mismo en pantalla y en impresión), que
  // alimenta el canvas de respaldo de abajo. Existe porque en la práctica
  // -probado con capturas reales, no solo en teoría- ni los tiles ni el
  // overlay vectorial de Leaflet sobreviven confiablemente el pipeline de
  // impresión de Chrome (ver \`.leaflet-map-pane\` en \`@media print\`): a veces
  // ninguno de los dos aparece, a veces solo uno, y no de forma consistente
  // entre exportaciones. Un \`<canvas>\` con los píxeles ya dibujados a mano SÍ
  // imprime siempre -es lo único que se probó estable en todos los intentos
  // anteriores-, así que ahora dibuja TAMBIÉN los tiles, no solo el recorrido.
  // Tamaño fijo y no el ancho real del mapa interactivo -que en pantalla
  // ocupa toda la tarjeta- para que este mapa oculto nunca necesite
  // redimensionarse ni pedir tiles nuevos a último momento.
  function crearMapaOculto(datos) {
    var contenedorOculto = document.createElement('div');
    contenedorOculto.style.position = 'absolute';
    contenedorOculto.style.top = '-9999px';
    contenedorOculto.style.left = '-9999px';
    contenedorOculto.style.width = '640px';
    contenedorOculto.style.height = '520px';
    document.body.appendChild(contenedorOculto);
    var mapaOculto = L.map(contenedorOculto, {
      zoomControl: false, attributionControl: false, dragging: false,
      scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false,
      keyboard: false, touchZoom: false, fadeAnimation: false, zoomAnimation: false,
    });
    var tiles = agregarRecorrido(mapaOculto, datos, false);
    return { mapa: mapaOculto, tiles: tiles, contenedor: contenedorOculto };
  }

  // Mapas interactivos ya creados. Hacen falta para \`invalidateSize()\`: los del
  // panel "Por plaga" nacen dentro de una pestaña OCULTA, y Leaflet no puede
  // medir un contenedor sin alto -se queda en gris hasta que se le avisa-.
  var mapasInteractivos = [];

  var mapasListos = [];
  // Redibujados de respaldo para imprimir -uno por mapa, ver el porque en
  // \`crearMapaOculto\`-.
  var redibujosParaImprimir = [];
  document.querySelectorAll('.rp-lote-mapa[data-mapa]').forEach(function (contenedor) {
    var datos = JSON.parse(contenedor.dataset.mapa);

    // Mapa interactivo (zoom/arrastre), para revisar el detalle en pantalla,
    // a todo el ancho de la tarjeta. Sin \`scrollWheelZoom\`: en una tarjeta
    // dentro de una página larga, la rueda del mouse debería seguir bajando
    // la página, no zoomeando el mapa. Se oculta al imprimir -\`@media
    // print\`- y se reemplaza por el canvas de abajo.
    var mapa = mapasInteractivos[mapasInteractivos.length] = L.map(contenedor, {
      zoomControl: true, attributionControl: false, dragging: true,
      scrollWheelZoom: false, doubleClickZoom: true, boxZoom: true,
      keyboard: true, touchZoom: true,
    });
    agregarRecorrido(mapa, datos, true);

    var oculto = crearMapaOculto(datos);

    var canvasImprimir = document.createElement('canvas');
    canvasImprimir.className = 'rp-lote-mapa-print-canvas';
    contenedor.appendChild(canvasImprimir);

    // Dibuja en el canvas de respaldo TANTO los tiles del mapa oculto -uno
    // por uno, con \`drawImage\` sobre la imagen ya cargada- COMO el recorrido,
    // usando sus coordenadas de píxel -fijas, porque el mapa oculto nunca
    // cambia de tamaño-. \`drawImage\` sobre una imagen sin CORS "mancha" el
    // canvas para lecturas de píxeles (\`getImageData\`), pero no para
    // pintarlo o imprimirlo, que es todo lo que este canvas necesita hacer.
    function redibujarParaImprimir() {
      var tam = oculto.mapa.getSize();
      var dpr = window.devicePixelRatio || 1;
      canvasImprimir.width = tam.x * dpr;
      canvasImprimir.height = tam.y * dpr;

      var ctx = canvasImprimir.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, tam.x, tam.y);

      var rectContenedor = oculto.contenedor.getBoundingClientRect();
      oculto.contenedor.querySelectorAll('img.leaflet-tile-loaded').forEach(function (img) {
        var r = img.getBoundingClientRect();
        try {
          ctx.drawImage(img, r.left - rectContenedor.left, r.top - rectContenedor.top, r.width, r.height);
        } catch (e) {
          // Un solo tile fallando (imagen rota, CORS estricto) no debe
          // tumbar el resto del dibujado.
        }
      });

      datos.segmentos.forEach(function (segmento) {
        ctx.beginPath();
        segmento.forEach(function (p, i) {
          var pt = oculto.mapa.latLngToContainerPoint([p.lat, p.lng]);
          if (i === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        });
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#1E88E5';
        ctx.globalAlpha = 0.85;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      datos.marcadores.forEach(function (m) {
        var pt = oculto.mapa.latLngToContainerPoint([m.lat, m.lng]);
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = m.color;
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
      });
    }

    redibujosParaImprimir.push(redibujarParaImprimir);
    // Listo para imprimir apenas los tiles del mapa OCULTO -no el
    // interactivo, que no se usa para imprimir- terminan de cargar.
    mapasListos.push(new Promise(function (resolver) {
      oculto.tiles.once('load', function () {
        redibujarParaImprimir();
        resolver();
      });
    }));
  });
  window.__rpMapasListos = Promise.all(mapasListos);

  // Redibuja justo antes de imprimir tambien -no solo al cargar-, por si el
  // usuario tarda en llegar al diálogo de impresión y algo cambió (poco
  // probable, dado que el mapa oculto nunca se redimensiona, pero es gratis).
  // Corre para CUALQUIER impresión -el botón "Exportar PDF" del topbar espera
  // \`__rpMapasListos\` antes de llamar \`window.print()\`, ver \`_buildTopbar\`-.
  window.addEventListener('beforeprint', function () {
    redibujosParaImprimir.forEach(function (redibujar) {
      redibujar();
    });
  });
})();`;
}

/**
 * Abre el diálogo de impresión solo, apenas el documento está listo.
 *
 * No basta con llamar `print()` al cargar: el reporte trae fuentes web (DM Sans
 * / DM Mono) y, si el diálogo se abre antes de que resuelvan, el navegador mide
 * la página con las tipografías de respaldo y la vista previa sale con otro
 * salto de página que el documento final. Por eso espera a `document.fonts`.
 *
 * Ese esperar lleva un tope: si las fuentes no llegan (sin red, bloqueadas), es
 * preferible imprimir con las de respaldo que dejar al usuario mirando una
 * pestaña que nunca abre el diálogo.
 */
function _buildAutoImprimirScript(): string {
  return `
(function () {
  // Antes solo esperaba fuentes web; ahora tambien los mini-mapas de
  // recorrido (window.__rpMapasListos, definido en el script principal) -sin
  // esto el dialogo de impresion podia abrirse con los tiles a medio cargar.
  var TOPE_ESPERA_MS = 3000;

  function abrirDialogo() {
    var fuentes =
      document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve();
    var mapas = window.__rpMapasListos || Promise.resolve();
    var tope = new Promise(function (resolver) {
      setTimeout(resolver, TOPE_ESPERA_MS);
    });
    Promise.race([Promise.all([fuentes, mapas]), tope]).then(function () {
      // Doble rAF: el primero corre al final del frame donde se termino de
      // pintar (fuentes + mapas); el segundo solo se dispara despues de que
      // ESE frame ya se compuso en pantalla. Con uno solo, la vista previa de
      // impresion podia tomarse a mitad del pintado del canvas del mapa -el
      // mismo motivo por el que antes hacia falta exportar dos veces-.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          window.print();
        });
      });
    });
  }

  if (document.readyState === 'complete') abrirDialogo();
  else window.addEventListener('load', abrirDialogo);
})();`;
}

/** Hoja de estilos completa del documento. Va embebida porque el reporte no
 *  comparte origen ni bundle con la app. */
function _buildEstilos(): string {
  return `
:root {
  --rp-ok: ${ESTADO_COLOR.ok};
  --rp-warn: ${ESTADO_COLOR.warn};
  --rp-alert: ${ESTADO_COLOR.alert};
  --rp-gray-50: #f5f4f0;
  --rp-gray-100: #e4e3dc;
  --rp-gray-200: #c8c6bc;
  --rp-gray-400: #8c8a82;
  --rp-gray-600: #5c5b56;
  --rp-gray-900: #28271f;
  --rp-font: 'DM Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
  --rp-mono: 'DM Mono', ui-monospace, 'Cascadia Mono', monospace;
}
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--rp-font); background: var(--rp-gray-50); color: var(--rp-gray-900); font-size: 13px; line-height: 1.45; }

.rp-topbar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 12px; height: 48px; padding: 0 20px; background: #fff; border-bottom: 1px solid var(--rp-gray-100); }
.rp-brand { font-weight: 600; letter-spacing: .02em; }
.rp-brand strong { color: #0095E2; }
.rp-topbar-sep { width: 1px; height: 20px; background: var(--rp-gray-100); }
.rp-breadcrumb { font-size: 12px; color: var(--rp-gray-600); }
.rp-spacer { flex: 1; }
.rp-btn-print { font-family: var(--rp-font); font-size: 12px; font-weight: 500; color: #fff; background: #0095E2; border: 0; border-radius: 8px; padding: 8px 14px; cursor: pointer; }
.rp-btn-print:hover { background: #0079b8; }

/* Ancho generoso: en pantalla lo que más se agradece es que entren más
   columnas de la matriz y más tarjetas por fila. El tope evita que en un
   monitor ultra-ancho las filas de la tabla queden imposibles de seguir con la
   vista. En impresión no ata: la página A4 útil ronda los 700px. */
.rp-page { max-width: 1600px; margin: 0 auto; padding: 24px 28px 64px; }
/* Empotrado en la app: el fondo lo pone la página contenedora y no hay
   topbar propia, así que sobra el aire de arriba. */
.rp-embebido { background: transparent; }
.rp-embebido .rp-page { padding-top: 16px; }
.rp-header { display: flex; align-items: flex-start; gap: 16px; padding-bottom: 16px; border-bottom: 2px solid var(--rp-gray-900); }
.rp-title { font-size: 22px; font-weight: 600; letter-spacing: -.01em; }
.rp-meta { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 6px; font-size: 12px; color: var(--rp-gray-600); }
.rp-badges { margin-left: auto; display: flex; gap: 6px; flex-shrink: 0; }
.rp-badge { font-size: 11px; font-weight: 500; padding: 4px 10px; border-radius: 20px; background: var(--rp-gray-100); color: var(--rp-gray-600); white-space: nowrap; }
.rp-badge--week { background: var(--rp-gray-900); color: #fff; }

.rp-section-label { font-size: 10px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: var(--rp-gray-400); margin: 28px 0 10px; }
.rp-section-label--sm { margin: 14px 0 8px; }
.rp-vacio { font-size: 12px; color: var(--rp-gray-400); padding: 12px 0; }

.rp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; }
.rp-kpi { background: #fff; border: 1px solid var(--rp-gray-100); border-radius: 10px; padding: 14px; }
.rp-kpi--alert { border-color: var(--rp-alert); }
.rp-kpi--warn { border-color: var(--rp-warn); }
.rp-kpi-label { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--rp-gray-400); }
.rp-kpi-value { font-family: var(--rp-mono); font-size: 28px; font-weight: 500; margin-top: 4px; }
.rp-kpi-value--texto { font-family: var(--rp-font); font-size: 15px; font-weight: 600; }
.rp-kpi-sub { font-size: 11px; color: var(--rp-gray-400); margin-top: 2px; }

.rp-matriz { background: #fff; border: 1px solid var(--rp-gray-100); border-radius: 10px; overflow: hidden; }
.rp-mx-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 14px; border-bottom: 1px solid var(--rp-gray-100); }
.rp-mx-title { font-size: 13px; font-weight: 600; }
.rp-mx-sub { font-size: 11px; color: var(--rp-gray-400); }
.rp-mx-leyenda { display: flex; gap: 12px; margin-left: auto; font-size: 11px; color: var(--rp-gray-600); }
.rp-mx-leyenda-item { display: inline-flex; align-items: center; gap: 5px; }
.rp-mx-leyenda-item .rp-mx-cell { min-width: 16px; padding: 3px 0; }
.rp-mx-scroll { overflow-x: auto; padding: 4px; }
.rp-mx-table { width: 100%; border-collapse: collapse; }
.rp-mx-table th { font-size: 10px; font-weight: 600; color: var(--rp-gray-600); padding: 10px 4px; text-align: center; border-bottom: 1px solid var(--rp-gray-100); }
.rp-mx-th { display: inline-block; max-width: 74px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rp-mx-table td { padding: 6px 4px; text-align: center; border-bottom: 1px solid var(--rp-gray-50); }
.rp-mx-lote { text-align: left !important; padding-left: 10px !important; min-width: 150px; }
.rp-mx-lote-nombre { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; }
.rp-mx-lote-meta { display: block; font-size: 10px; color: var(--rp-gray-400); margin-top: 1px; }
.rp-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.rp-mx-cell { display: inline-block; min-width: 40px; padding: 3px 6px; border-radius: 5px; font-family: var(--rp-mono); font-size: 11px; }
.rp-mx-cell--ok { background: #f0fdf4; color: #15803d; }
.rp-mx-cell--warn { background: #fffbeb; color: #92400e; }
.rp-mx-cell--alert { background: #fef2f2; color: #991b1b; }
.rp-mx-cell--cero { color: var(--rp-gray-200); }


.rp-btn-quitar { margin-top: 8px; font-family: var(--rp-font); font-size: 11px; color: var(--rp-gray-600); background: none; border: 1px solid var(--rp-gray-100); border-radius: 6px; padding: 4px 10px; cursor: pointer; }

.rp-tabs { display: flex; gap: 4px; margin: 28px 0 12px; border-bottom: 1px solid var(--rp-gray-100); }
.rp-tab { font-family: var(--rp-font); font-size: 12px; font-weight: 500; color: var(--rp-gray-400); background: none; border: 0; border-bottom: 2px solid transparent; padding: 8px 14px; cursor: pointer; }
.rp-tab--active { color: var(--rp-gray-900); border-bottom-color: var(--rp-gray-900); }
.rp-panel { display: none; }
.rp-panel--active { display: block; }

.rp-lote { background: #fff; border: 1px solid var(--rp-gray-100); border-left: 4px solid var(--rp-gray-200); border-radius: 10px; margin-bottom: 12px; overflow: hidden; }
.rp-lote--alert { border-left-color: var(--rp-alert); }
.rp-lote--warn { border-left-color: var(--rp-warn); }
.rp-lote--ok { border-left-color: var(--rp-ok); }
.rp-lote-header { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-bottom: 1px solid var(--rp-gray-100); }
.rp-lote-nombre { font-size: 15px; font-weight: 600; }
.rp-lote-meta { font-size: 11px; color: var(--rp-gray-400); margin-top: 1px; }
.rp-lote-badge { margin-left: auto; font-size: 10px; font-weight: 600; color: #fff; padding: 4px 10px; border-radius: 20px; white-space: nowrap; }
.rp-lote-body { padding: 14px 16px; }
/* Miniatura del recorrido de este lote, al pie de su tarjeta -ver
   \`_buildDatosMapaLote\`-. Leaflet necesita una altura real ANTES de
   inicializarse, a diferencia de una \`img\`, que se ajustaba sola. A todo
   el ancho de la tarjeta EN PANTALLA a propósito -el recorte a un ancho fijo
   para que coincidiera con el de impresión se descartó: dejaba el mapa
   angosto en pantalla, y de todos modos no era la causa real del problema
   de impresión (ver \`_buildScript\`, \`crearMapaOculto\`). */
.rp-lote-mapa { position: relative; margin-top: 10px; height: 520px; border-radius: 8px; overflow: hidden; background: var(--rp-gray-100); }
/* Canvas de respaldo para imprimir (ver _buildScript): oculto en pantalla,
   donde mandan los vectores interactivos de Leaflet. z-index por encima de
   cualquier pane de Leaflet (el mas alto que usan, popup-pane, es 700). */
.rp-lote-mapa-print-canvas { display: none; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; pointer-events: none; }
.rp-plaga-stats { display: flex; justify-content: space-between; gap: 8px; margin-top: 8px; padding-top: 7px; border-top: 1px solid var(--rp-gray-100); font-size: 10px; color: var(--rp-gray-400); }
.rp-plaga-stats b { color: var(--rp-gray-700, #343a40); font-weight: 600; }
.rp-bp-mapa { display: flex; gap: 12px; margin-top: 12px; align-items: stretch; }
.rp-bp-mapa .rp-lote-mapa { flex: 1; margin-top: 0; height: 440px; }
.rp-escala { width: 150px; flex-shrink: 0; font-size: 10px; color: var(--rp-gray-400); display: flex; flex-direction: column; gap: 6px; }
.rp-escala-titulo { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--rp-gray-400); margin-bottom: 2px; }
.rp-escala-item { display: inline-flex; align-items: center; gap: 6px; }
.rp-escala-nota { margin-top: 4px; font-style: italic; line-height: 1.35; }
.rp-mapa-finca { margin-bottom: 18px; }
.rp-mapa-finca-wrap { position: relative; }
.rp-mapa-finca-pie { position: absolute; left: 12px; bottom: 12px; z-index: 500; background: rgba(0,0,0,.55); color: #fff; font-size: 11px; padding: 4px 10px; border-radius: 6px; }
.rp-mapa-finca-leyenda { margin-top: 8px; }
.rp-feno-bloque { margin-bottom: 14px; }
.rp-feno-barra { display: flex; height: 12px; border-radius: 6px; overflow: hidden; background: var(--rp-gray-50); margin: 6px 0 8px; }
.rp-feno-seg { height: 100%; }
.rp-feno-leyenda { display: flex; flex-wrap: wrap; gap: 14px; font-size: 11px; color: var(--rp-gray-400); }
.rp-feno-item { display: inline-flex; align-items: center; gap: 5px; }
.rp-feno-punto { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.rp-feno { display: flex; align-items: center; gap: 10px; font-size: 12px; }
.rp-feno-label { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--rp-gray-400); }
.rp-feno-valor { padding: 3px 10px; border-radius: 20px; background: var(--rp-gray-50); border: 1px solid var(--rp-gray-100); }
.rp-obs { font-size: 12px; color: var(--rp-gray-600); background: var(--rp-gray-50); border-radius: 8px; padding: 10px 12px; margin-top: 10px; }

.rp-plagas-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 10px; }
.rp-plaga { border: 1px solid var(--rp-gray-100); border-radius: 8px; padding: 12px; }
.rp-plaga-top { display: flex; align-items: baseline; gap: 8px; }
.rp-plaga-nombre { font-size: 12px; font-weight: 600; }
.rp-plaga-badge { margin-left: auto; font-size: 10px; font-weight: 600; white-space: nowrap; }
.rp-plaga-valor { font-family: var(--rp-mono); font-size: 24px; font-weight: 500; margin-top: 6px; }
.rp-plaga-sub { font-size: 10px; color: var(--rp-gray-400); }
.rp-plaga-tecnico { font-size: 10px; font-style: italic; color: var(--rp-gray-400); margin-top: 4px; }
.rp-barra { position: relative; height: 7px; border-radius: 4px; background: var(--rp-gray-100); margin-top: 10px; overflow: hidden; }
.rp-barra-fill { height: 100%; border-radius: 4px; }
.rp-barra-marca { position: absolute; top: -2px; width: 2px; height: 11px; background: var(--rp-gray-600); }
.rp-barra-labels { display: flex; justify-content: space-between; font-size: 9px; color: var(--rp-gray-400); margin-top: 4px; }

.rp-charts { display: grid; grid-template-columns: 2fr 1fr; gap: 12px; margin-bottom: 20px; }
.rp-chart-card { background: #fff; border: 1px solid var(--rp-gray-100); border-radius: 10px; padding: 14px; }
.rp-chart-title { font-size: 13px; font-weight: 600; }
.rp-chart-sub { font-size: 11px; color: var(--rp-gray-400); margin-bottom: 10px; }
.rp-svg { width: 100%; height: auto; }
.rp-svg--dona { max-width: 190px; margin: 0 auto; display: block; }
.rp-svg-tick { font-family: var(--rp-mono); font-size: 9px; fill: #8c8a82; }
.rp-svg-label { font-family: var(--rp-font); font-size: 10px; fill: #5c5b56; }
.rp-svg-dona-total { font-family: var(--rp-mono); font-size: 5px; fill: #28271f; }
.rp-svg-dona-sub { font-family: var(--rp-font); font-size: 2.4px; fill: #8c8a82; }
.rp-legend { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; font-size: 11px; color: var(--rp-gray-600); }
.rp-legend--vertical { flex-direction: column; gap: 5px; }
.rp-legend-item { display: inline-flex; align-items: center; gap: 5px; }
.rp-legend-swatch { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }

.rp-bp-section { background: #fff; border: 1px solid var(--rp-gray-100); border-radius: 10px; padding: 14px; margin-bottom: 12px; }
.rp-bp-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.rp-bp-title { font-size: 14px; font-weight: 600; }
.rp-bp-tecnico { font-size: 11px; font-style: italic; color: var(--rp-gray-400); }
.rp-bp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px; }
.rp-bp { border: 1px solid var(--rp-gray-100); border-radius: 8px; padding: 10px; }
.rp-bp-lote { font-size: 12px; font-weight: 500; }
.rp-bp-valor { font-family: var(--rp-mono); font-size: 20px; font-weight: 500; }
.rp-bp-sub { font-size: 9px; color: var(--rp-gray-400); }

.rp-firma { background: #fff; border: 1px solid var(--rp-gray-100); border-radius: 10px; margin-top: 28px; padding: 16px; }
.rp-firma-title { font-size: 13px; font-weight: 600; margin-bottom: 12px; }
.rp-firma-body { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
.rp-firma-label { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--rp-gray-400); margin-bottom: 6px; }
.rp-textarea, .rp-input-fecha { width: 100%; font-family: var(--rp-font); font-size: 12px; color: var(--rp-gray-900); border: 1px solid var(--rp-gray-100); border-radius: 8px; padding: 8px 10px; resize: vertical; background: #fff; }
.rp-firma-linea { border-bottom: 1px solid var(--rp-gray-900); margin: 32px 0 6px; }
.rp-firma-agronomo { font-family: var(--rp-mono); font-size: 11px; color: var(--rp-gray-600); }

.rp-footer { text-align: center; font-size: 10px; color: var(--rp-gray-400); margin-top: 28px; }

@media (max-width: 860px) {
  .rp-charts, .rp-firma-body { grid-template-columns: 1fr; }
}

@media print {
  /* Margen de página en cero A PROPÓSITO: el navegador dibuja su encabezado y
     su pie (título, fecha, URL y número de página) DENTRO del margen de
     \`@page\`. Sin margen no tiene dónde pintarlos y desaparecen — que es lo que
     queremos, porque si no cada hoja del informe sale estampada con la URL
     interna \`blob:http://…\`, que no significa nada para quien lo recibe.
     El margen visual lo aporta el padding de \`.rp-page\`. */
  @page { margin: 0; }
  body { background: #fff; font-size: 11px; }
  .no-print { display: none !important; }
  /* El margen de impresión, ahora nuestro. El padding lateral se respeta en
     TODAS las hojas; el vertical solo abre la primera y cierra la última, así
     que los bloques llevan además su propio aire interno para no arrancar
     pegados al borde cuando caen al principio de una hoja. */
  .rp-page { max-width: none; padding: 14mm 12mm 16mm; }
  /* En papel no hay tabs: ambos paneles se imprimen uno tras otro. */
  .rp-panel { display: block !important; }
  .rp-lote, .rp-chart-card, .rp-bp-section, .rp-firma, .rp-matriz { break-inside: avoid; page-break-inside: avoid; }
  .rp-textarea, .rp-input-fecha { border: 0; padding: 0; }

  /* Tarjetas de lote compactas en papel. Sin esto cada una mide casi una
     página entera y, con \`break-inside: avoid\`, ninguna puede compartir hoja:
     cada lote arranca en página nueva y desperdicia el resto de la anterior. */
  .rp-plagas-grid { grid-template-columns: repeat(4, 1fr); gap: 6px; }
  /* Los botones +/- no sirven en papel -no hay como hacer click en una
     hoja impresa-, y sin ocultarlos quedan flotando sobre el mapa. */
  .leaflet-control-zoom { display: none !important; }
  /* Ni los tiles ni el overlay vectorial de Leaflet (polilinea + circleMarker)
     sobreviven confiablemente la impresion real de Chrome -ver el comentario
     en _buildScript, funcion crearMapaOculto-; se oculta TODO lo que Leaflet
     dibuja (tiles, vectores, marcadores) y se muestra en su lugar el canvas
     de respaldo, dibujado a mano con los tiles y los puntos ya rasterizados. */
  .leaflet-map-pane { visibility: hidden !important; }
  .rp-lote-mapa-print-canvas { display: block !important; }
  .rp-plaga { padding: 8px; }
  .rp-plaga-valor { font-size: 17px; margin-top: 3px; }
  .rp-plaga-tecnico { margin-top: 2px; }
  .rp-barra { margin-top: 6px; }
  .rp-lote-header, .rp-lote-body { padding: 10px 12px; }
  .rp-obs { margin-top: 8px; padding: 8px 10px; }
  .rp-bp-grid { grid-template-columns: repeat(5, 1fr); gap: 6px; }
  .rp-bp { padding: 8px; }
  /* Los colores del semáforo son información, no decoración: deben salir
     impresos aunque el navegador quiera ahorrar tinta. */
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}`;
}
